import validator from 'validator';

// The form of the "Promemoria scadenze documenti" dialog, kept apart from the
// components so its rules read in one place: what the server sends, what the
// form edits, what goes back. Preavvisi and destinatari are edited as text, the
// way an input hands them over, and are converted only on the way out.

export const MASSIMO_PREAVVISI = 3;
export const MASSIMO_DESTINATARI = 2;

// Not in the PRD: the server caps a preavviso at two digits, so that a typo
// (700 for 70) cannot start notices nobody expects. The form says it first.
export const PREAVVISO_MASSIMO_GIORNI = 99;

// What the form proposes when the esercizio has no preavvisi yet (PRD: "un
// solo preavviso a 7 giorni"). The form's alone: neither the server nor the
// schema has a default, so an esercizio never configured comes with none.
export const PREAVVISO_PROPOSTO = 7;

const INTERO = /^\d+$/;

const PREAVVISO_NON_VALIDO = `Inserisci un numero intero da 1 a ${PREAVVISO_MASSIMO_GIORNI}`;

export const formDaConfigurazione = (configurazione) => ({
  attivo: configurazione.attivo,
  preavvisi: (configurazione.preavvisi.length
    ? configurazione.preavvisi
    : [PREAVVISO_PROPOSTO]
  ).map(String),
  destinatari: [...configurazione.destinatari],
});

/**
 * The body of the save. With the service off the dialog hides preavvisi and
 * destinatari, so turning it off changes only the switch: the rest goes back as
 * it was saved, whatever was typed before switching. An esercizio never
 * configured has no preavvisi to send back, and the server wants one to three
 * even with the service off: it gets the proposed one.
 *
 * Addresses leave in lower case: the dialog refuses the same mailbox twice
 * whatever its case, and the server's daily run matches the addresses it
 * already wrote to exactly, so one spelling per mailbox keeps the two agreed.
 */
export const richiestaDaForm = (form, salvata) => {
  if (!form.attivo) {
    return {
      attivo: false,
      preavvisi: salvata.preavvisi.length
        ? salvata.preavvisi
        : [PREAVVISO_PROPOSTO],
      destinatari: salvata.destinatari,
    };
  }
  return {
    attivo: true,
    preavvisi: form.preavvisi.map(Number),
    destinatari: form.destinatari
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean),
  };
};

// A decimal is refused here, not on the server (BSS-1968): the backend would
// truncate 7.5 to 7, like every other integer field of the service.
const erroriPreavvisi = (preavvisi) => {
  const visti = new Set();
  return preavvisi.map((testo) => {
    const valore = testo.trim();
    if (!INTERO.test(valore)) {
      return PREAVVISO_NON_VALIDO;
    }
    const giorni = Number(valore);
    if (giorni < 1 || giorni > PREAVVISO_MASSIMO_GIORNI) {
      return PREAVVISO_NON_VALIDO;
    }
    if (visti.has(giorni)) {
      return 'Preavviso già presente';
    }
    visti.add(giorni);
    return null;
  });
};

// The same mailbox twice is refused here and on the server (BSS-1869): case
// and surrounding spaces do not tell two mailboxes apart.
const erroriDestinatari = (destinatari) => {
  const visti = new Set();
  const errori = destinatari.map((email) => {
    const valore = email.trim();
    if (!valore) {
      return null;
    }
    if (!validator.isEmail(valore)) {
      return 'Formato email non valido';
    }
    const casella = valore.toLowerCase();
    if (visti.has(casella)) {
      return 'Destinatario già presente';
    }
    visti.add(casella);
    return null;
  });
  if (!destinatari.some((email) => email.trim())) {
    errori[0] = 'Inserisci almeno un destinatario';
  }
  return errori;
};

/**
 * The errors of the form, by field; an empty object when it can be saved. Each
 * list has one entry per row, `null` where the row is fine. With the service
 * off nothing is checked: nothing typed there is saved.
 */
export const erroriPromemoria = (form) => {
  if (!form.attivo) {
    return {};
  }
  const errori = {};
  const preavvisi = erroriPreavvisi(form.preavvisi);
  if (preavvisi.some(Boolean)) {
    errori.preavvisi = preavvisi;
  }
  const destinatari = erroriDestinatari(form.destinatari);
  if (destinatari.some(Boolean)) {
    errori.destinatari = destinatari;
  }
  return errori;
};

const elenco = (valori) =>
  valori.length === 1
    ? valori[0]
    : `${valori.slice(0, -1).join(', ')} e ${valori[valori.length - 1]}`;

const preavvisiAllAttivazione = (giorni) => {
  if (!giorni.length) {
    return 'potrai impostare i preavvisi';
  }
  return giorni.length === 1
    ? `il preavviso è impostato a ${giorni[0]} giorni`
    : `i preavvisi sono impostati a ${elenco(giorni)} giorni`;
};

/**
 * The line of the dialog with the service off: what switching it on brings
 * back. That is what the form holds, so the proposed 7 days only for an
 * esercizio without preavvisi, and the saved ones otherwise.
 */
export const spiegazioneSpento = (form) => {
  const preavvisi = preavvisiAllAttivazione(
    form.preavvisi.map((testo) => testo.trim()).filter(Boolean),
  );
  const destinatari = form.destinatari.some((email) => email.trim())
    ? 'ritrovi i destinatari inseriti'
    : 'potrai aggiungere i destinatari';
  return `Con il servizio spento non parte nessun avviso. Attivandolo, ${preavvisi} e ${destinatari}.`;
};

/**
 * The line under the toolbar of Documenti: "Promemoria attivo · avvisi 30 e 7
 * giorni prima della scadenza · 2 destinatari". `null` with the service off.
 */
export const riepilogoPromemoria = (configurazione) => {
  if (!configurazione?.attivo) {
    return null;
  }
  const { preavvisi } = configurazione;
  const destinatari = configurazione.destinatari.length;
  return [
    'Promemoria attivo',
    `${preavvisi.length === 1 ? 'avviso' : 'avvisi'} ${elenco(
      preavvisi,
    )} giorni prima della scadenza`,
    `${destinatari} ${destinatari === 1 ? 'destinatario' : 'destinatari'}`,
  ].join(' · ');
};
